import { router } from '@inertiajs/react'
import { useState } from 'react'
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ClockIcon,
  EyeIcon,
  ListIcon,
  PlusIcon,
  SearchIcon,
  UserIcon,
  XIcon,
} from 'lucide-react'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Pagination, PaginationContent, PaginationEllipsis, PaginationItem } from '@/components/ui/pagination'
import { Badge } from '@/components/ui/badge'
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group'
import { AppHeader } from '@/components/app-header'
import { AppointmentsCalendarView } from '@/components/appointments-calendar-view'
import { AppointmentDialog } from '@/components/appointment-dialog'
import { AppointmentActionsMenu } from '@/components/appointment-actions-menu'

interface Appointment {
  id: number
  patient: {
    id: number
    display_name: string
    phone_number: string
  }
  care_case_id: number
  site: {
    id: number
    name: string
  }
  appointment_type: string
  type_label: string
  scheduled_at: string
  formatted_date: string
  formatted_time: string
  formatted_datetime: string
  duration_minutes: number
  duration_hours: number
  end_time: string
  status: string
  status_label: string
  location: string | null
  reason: string | null
  notes: string | null
  cancellation_reason: string | null
  cancelled_at: string | null
  completed_at: string | null
  created_at: string
  updated_at: string
  upcoming: boolean
  past: boolean
}

interface Patient {
  id: number
  display_name: string
  phone_number: string
  site_id: number
  has_care_case: boolean
}

interface Site {
  id: number
  name: string
}

interface PaginationData {
  page: number
  pages: number
  count: number
  from: number
  to: number
  prev: number | null
  next: number | null
}

interface Filters {
  patient_id?: number | null
  site_id?: number | null
  status?: string | null
  appointment_type?: string | null
  start_date?: string | null
  end_date?: string | null
}

interface AdminAppointmentsIndexProps {
  auth: {
    user: {
      id: number
      name: string
      email: string
      admin: boolean
    }
  }
  appointments: Appointment[]
  pagination: PaginationData
  filters: Filters
  sites?: Site[]
  patients?: Patient[]
}

export default function AdminAppointmentsIndex({
  auth,
  appointments,
  pagination,
  filters,
  sites = [],
  patients = []
}: AdminAppointmentsIndexProps) {
  const [viewMode, setViewMode] = useState<'list' | 'calendar'>('list')
  const [showCreateDialog, setShowCreateDialog] = useState(false)
  const [editingAppointment, setEditingAppointment] = useState<Appointment | null>(null)

  const handleFilterChange = (filterName: string, value: string | undefined) => {
    router.get('/admin/appointments', {
      ...filters,
      [filterName]: value || undefined,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['appointments', 'pagination', 'filters'],
    })
  }

  const handlePageChange = (page: number) => {
    router.get('/admin/appointments', {
      ...filters,
      page,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['appointments', 'pagination'],
    })
  }

  const clearFilters = () => {
    router.get('/admin/appointments', {}, {
      preserveState: true,
      preserveScroll: true,
      only: ['appointments', 'pagination', 'filters'],
    })
  }

  const handleQuickFilter = (filterType: 'today' | 'week' | 'month' | 'upcoming') => {
    const today = new Date()
    today.setHours(0, 0, 0, 0)

    let start_date: string | undefined
    let end_date: string | undefined

    if (filterType === 'today') {
      start_date = today.toISOString().split('T')[0]
      end_date = today.toISOString().split('T')[0]
    } else if (filterType === 'week') {
      const weekEnd = new Date(today)
      weekEnd.setDate(today.getDate() + 7)
      start_date = today.toISOString().split('T')[0]
      end_date = weekEnd.toISOString().split('T')[0]
    } else if (filterType === 'month') {
      const monthEnd = new Date(today)
      monthEnd.setMonth(today.getMonth() + 1)
      start_date = today.toISOString().split('T')[0]
      end_date = monthEnd.toISOString().split('T')[0]
    }

    router.get('/admin/appointments', {
      ...filters,
      start_date,
      end_date,
    }, {
      preserveState: true,
      preserveScroll: true,
      only: ['appointments', 'pagination', 'filters'],
    })
  }

  const hasActiveFilters = filters.site_id || filters.status || filters.appointment_type || filters.start_date || filters.end_date

  const getStatusBadgeVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
    switch (status) {
      case 'completed':
        return 'default'
      case 'confirmed':
        return 'secondary'
      case 'cancelled':
        return 'destructive'
      case 'no_show':
        return 'destructive'
      case 'scheduled':
      default:
        return 'outline'
    }
  }

  const getTypeBadgeColor = (type: string): string => {
    switch (type) {
      case 'initial_consultation':
        return 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300'
      case 'follow_up_visit':
        return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300'
      case 'lab_review':
        return 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
      case 'check_in':
        return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-300'
      case 'phone_call':
        return 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300'
      case 'telehealth':
        return 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300'
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    }
  }

  return (
    <div className="flex min-h-screen w-full flex-col bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950">
      <div className="flex flex-col sm:gap-4 sm:py-4">
        <AppHeader breadcrumbs={[{ label: 'Appointments' }]} />

        <main className="grid flex-1 items-start gap-4 p-4 sm:px-6 sm:py-0 md:gap-8">
          <Card className="shadow-lg border-0 bg-white/80 backdrop-blur-sm dark:bg-gray-900/80">
            <CardHeader className="bg-gradient-to-r from-purple-50/50 to-indigo-50/50 dark:from-purple-950/30 dark:to-indigo-950/30 border-b">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-gradient-to-br from-purple-600 to-indigo-600 shadow-md">
                    <CalendarIcon className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-2xl font-bold">Appointments</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">Schedule and manage patient visits</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <ToggleGroup
                    type="single"
                    value={viewMode}
                    onValueChange={(value) => value && setViewMode(value as 'list' | 'calendar')}
                    className="border border-purple-200 dark:border-purple-800"
                  >
                    <ToggleGroupItem
                      value="list"
                      aria-label="List view"
                      className="data-[state=on]:bg-gradient-to-r data-[state=on]:from-purple-100 data-[state=on]:to-indigo-100 dark:data-[state=on]:from-purple-950 dark:data-[state=on]:to-indigo-950"
                    >
                      <ListIcon className="h-4 w-4" />
                    </ToggleGroupItem>
                    <ToggleGroupItem
                      value="calendar"
                      aria-label="Calendar view"
                      className="data-[state=on]:bg-gradient-to-r data-[state=on]:from-purple-100 data-[state=on]:to-indigo-100 dark:data-[state=on]:from-purple-950 dark:data-[state=on]:to-indigo-950"
                    >
                      <CalendarIcon className="h-4 w-4" />
                    </ToggleGroupItem>
                  </ToggleGroup>
                  <Button
                    onClick={() => setShowCreateDialog(true)}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md"
                  >
                    <PlusIcon className="mr-2 h-4 w-4" />
                    New Appointment
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6">
              {/* Quick Filters */}
              <div className="mb-6 flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickFilter('today')}
                  className="border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50 transition-all"
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickFilter('week')}
                  className="border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50 transition-all"
                >
                  This Week
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickFilter('month')}
                  className="border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50 transition-all"
                >
                  This Month
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleQuickFilter('upcoming')}
                  className="border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50 transition-all"
                >
                  Upcoming
                </Button>
              </div>

              {/* Filters */}
              <div className="mb-6 rounded-lg bg-gradient-to-br from-purple-50/30 to-indigo-50/30 dark:from-purple-950/20 dark:to-indigo-950/20 p-4 border border-purple-100 dark:border-purple-900/30">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
                  <Select
                    value={filters.site_id?.toString() || 'all'}
                    onValueChange={(value) => handleFilterChange('site_id', value === 'all' ? undefined : value)}
                  >
                    <SelectTrigger className="border-purple-200 dark:border-purple-800">
                      <SelectValue placeholder="All Sites" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Sites</SelectItem>
                      {sites.map((site) => (
                        <SelectItem key={site.id} value={site.id.toString()}>
                          {site.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.status || 'all'}
                    onValueChange={(value) => handleFilterChange('status', value === 'all' ? undefined : value)}
                  >
                    <SelectTrigger className="border-purple-200 dark:border-purple-800">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="scheduled">Scheduled</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                      <SelectItem value="no_show">No Show</SelectItem>
                    </SelectContent>
                  </Select>

                  <Select
                    value={filters.appointment_type || 'all'}
                    onValueChange={(value) => handleFilterChange('appointment_type', value === 'all' ? undefined : value)}
                  >
                    <SelectTrigger className="border-purple-200 dark:border-purple-800">
                      <SelectValue placeholder="All Types" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Types</SelectItem>
                      <SelectItem value="initial_consultation">Initial Consultation</SelectItem>
                      <SelectItem value="follow_up_visit">Follow-up Visit</SelectItem>
                      <SelectItem value="lab_review">Lab Review</SelectItem>
                      <SelectItem value="check_in">Check-in</SelectItem>
                      <SelectItem value="phone_call">Phone Call</SelectItem>
                      <SelectItem value="telehealth">Telehealth</SelectItem>
                    </SelectContent>
                  </Select>

                  {hasActiveFilters && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={clearFilters}
                      className="h-10 border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50 transition-all"
                    >
                      <XIcon className="mr-2 h-4 w-4" />
                      Clear Filters
                    </Button>
                  )}
                </div>
              </div>

              {viewMode === 'calendar' ? (
                <AppointmentsCalendarView appointments={appointments} />
              ) : (
                <>
                  {/* Results Count */}
                  <div className="mb-4 text-sm font-medium text-muted-foreground">
                    Showing <span className="text-purple-600 dark:text-purple-400">{pagination.from}</span> to <span className="text-purple-600 dark:text-purple-400">{pagination.to}</span> of <span className="text-purple-600 dark:text-purple-400">{pagination.count}</span> appointments
                  </div>

                  {/* Appointments Table */}
                  <div className="rounded-lg border border-purple-100 dark:border-purple-900/30 overflow-hidden shadow-sm">
                    <Table>
                      <TableHeader>
                        <TableRow className="bg-gradient-to-r from-purple-50 to-indigo-50 dark:from-purple-950/50 dark:to-indigo-950/50 hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50">
                          <TableHead className="font-semibold">Date & Time</TableHead>
                          <TableHead className="font-semibold">Patient</TableHead>
                          <TableHead className="font-semibold">Type</TableHead>
                          <TableHead className="font-semibold">Location</TableHead>
                          <TableHead className="font-semibold">Duration</TableHead>
                          <TableHead className="font-semibold">Site</TableHead>
                          <TableHead className="font-semibold">Status</TableHead>
                          <TableHead className="text-right font-semibold">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {appointments.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} className="text-center py-12">
                              <div className="flex flex-col items-center gap-4">
                                <div className="flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br from-purple-100 to-indigo-100 dark:from-purple-950 dark:to-indigo-950">
                                  <CalendarIcon className="h-10 w-10 text-purple-600 dark:text-purple-400" />
                                </div>
                                <div>
                                  <p className="text-lg font-semibold text-foreground">No appointments found</p>
                                  <p className="text-sm text-muted-foreground mt-1">Try adjusting your search or filters</p>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          appointments.map((appointment) => (
                            <TableRow key={appointment.id} className="transition-all hover:bg-gradient-to-r hover:from-purple-50/50 hover:to-indigo-50/50 dark:hover:from-purple-950/30 dark:hover:to-indigo-950/30">
                              <TableCell className="py-4">
                                <div className="flex items-start gap-3">
                                  <div className="flex h-9 w-9 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 shadow-sm">
                                    <CalendarIcon className="h-4 w-4 text-white" />
                                  </div>
                                  <div>
                                    <div className="font-semibold">{appointment.formatted_date}</div>
                                    <div className="text-sm text-muted-foreground flex items-center gap-1">
                                      <ClockIcon className="h-3 w-3" />
                                      {appointment.formatted_time}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-4">
                                <div className="flex items-center gap-2">
                                  <UserIcon className="h-4 w-4 text-muted-foreground" />
                                  <div>
                                    <div className="font-semibold">{appointment.patient.display_name}</div>
                                    <div className="text-xs text-muted-foreground">
                                      {appointment.patient.phone_number}
                                    </div>
                                  </div>
                                </div>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge className={`${getTypeBadgeColor(appointment.appointment_type)} shadow-sm font-medium`}>
                                  {appointment.type_label}
                                </Badge>
                              </TableCell>
                              <TableCell className="py-4">
                                <span className="text-sm font-medium">
                                  {appointment.location || '—'}
                                </span>
                              </TableCell>
                              <TableCell className="py-4">
                                <span className="text-sm font-medium text-muted-foreground">
                                  {appointment.duration_minutes} min
                                </span>
                              </TableCell>
                              <TableCell className="py-4">
                                <span className="text-sm font-medium">{appointment.site.name}</span>
                              </TableCell>
                              <TableCell className="py-4">
                                <Badge variant={getStatusBadgeVariant(appointment.status)} className="shadow-sm font-medium">
                                  {appointment.status_label}
                                </Badge>
                              </TableCell>
                              <TableCell className="text-right py-4">
                                <div className="flex items-center justify-end gap-2">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => router.visit(`/admin/appointments/${appointment.id}`)}
                                    className="hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950 dark:hover:to-indigo-950 transition-all"
                                  >
                                    <EyeIcon className="h-4 w-4 mr-2" />
                                    View
                                  </Button>
                                  <AppointmentActionsMenu
                                    appointment={appointment}
                                    onEdit={() => setEditingAppointment(appointment)}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Pagination */}
                  {pagination.pages > 1 && (
                    <div className="mt-6">
                      <Pagination>
                        <PaginationContent>
                          <PaginationItem>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePageChange(pagination.page - 1)}
                              disabled={!pagination.prev}
                              className="border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50"
                            >
                              <ChevronLeftIcon className="h-4 w-4 mr-2" />
                              Previous
                            </Button>
                          </PaginationItem>

                          {[...Array(pagination.pages)].map((_, index) => {
                            const pageNum = index + 1
                            if (
                              pageNum === 1 ||
                              pageNum === pagination.pages ||
                              (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                            ) {
                              return (
                                <PaginationItem key={pageNum}>
                                  <Button
                                    variant={pageNum === pagination.page ? 'default' : 'outline'}
                                    size="sm"
                                    onClick={() => handlePageChange(pageNum)}
                                    className={pageNum === pagination.page
                                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 shadow-md'
                                      : 'border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50'}
                                  >
                                    {pageNum}
                                  </Button>
                                </PaginationItem>
                              )
                            } else if (pageNum === pagination.page - 2 || pageNum === pagination.page + 2) {
                              return <PaginationEllipsis key={pageNum} />
                            }
                            return null
                          })}

                          <PaginationItem>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handlePageChange(pagination.page + 1)}
                              disabled={!pagination.next}
                              className="border-purple-200 dark:border-purple-800 hover:bg-gradient-to-r hover:from-purple-50 hover:to-indigo-50 dark:hover:from-purple-950/50 dark:hover:to-indigo-950/50"
                            >
                              Next
                              <ChevronRightIcon className="h-4 w-4 ml-2" />
                            </Button>
                          </PaginationItem>
                        </PaginationContent>
                      </Pagination>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </main>
      </div>

      {/* Create/Edit Dialog */}
      <AppointmentDialog
        open={showCreateDialog || editingAppointment !== null}
        onClose={() => {
          setShowCreateDialog(false)
          setEditingAppointment(null)
        }}
        appointment={editingAppointment}
        patients={patients}
        mode={editingAppointment ? 'edit' : 'create'}
      />
    </div>
  )
}
